// @ts-strict-ignore
import type { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import type { EngagementViewsData } from '@empathybroker/tsclient-stats/dist/models/engagement/views/engagement-views-data'
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type { ActionContext, Store } from 'vuex'
import type { FeatureIdEnum } from '../../../enums'
import type {
  ActionClass,
  DateFilterSelectedValues,
  FeatureNode,
  FeaturesTree,
  TaggingState,
  TaggingViewData,
  TaggingViewDataItem,
  TaggingViewPayload,
} from '../../../models'
import type { FeatureConfigProvider } from '../../../providers/interfaces/feature-config.provider'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { CONTAINER } from '../../../config/inversify.config'

import { TYPES } from '../../../config/inversify.types'

import { TaggingTypeEnum } from '../../../enums'

import { TaggingUtils } from '../../../utils'
import { ANALYTICS_CONSTS } from '../../analytics/analytics.consts'
import { APP_CONSTS } from '../../app/app.consts'
import { TAGGING_CONSTS } from '../tagging.consts'

export class LoadViewsAction implements ActionClass {
  static async execute(
    this: void,
    { commit, rootGetters }: ActionContext<TaggingState, any>,
    payload: DateFilterSelectedValues,
  ): Promise<void> {
    const featuresTree: FeaturesTree =
      rootGetters[`${APP_CONSTS.moduleName}/${APP_CONSTS.getters.featuresTree}`]
    const data = await LoadViewsAction.getData(featuresTree, payload)
    commit(TAGGING_CONSTS.mutations.setData, {
      data,
      type: TaggingTypeEnum.VIEW,
    })
  }

  private static async getData(
    featuresTree: FeaturesTree,
    period: DateFilterSelectedValues,
  ): Promise<TaggingViewData> {
    const featuresTreeCopy = TaggingUtils.removeBrowseAnalyticsFromFeaturesTree(featuresTree)
    return Object.entries(featuresTreeCopy).reduce(
      async (
        dataPromise: Promise<TaggingViewData>,
        [domain, nodes]: [DomainEnum, FeatureNode[]],
      ) => {
        const data = await dataPromise
        const domainData = await LoadViewsAction.getDomainData(domain, nodes, data, period)
        data.push(...domainData)
        return data
      },
      Promise.resolve([]),
    )
  }

  private static async getDomainData(
    domain: DomainEnum,
    nodes: FeatureNode[],
    data: TaggingViewData,
    period: DateFilterSelectedValues,
  ): Promise<TaggingViewData> {
    return nodes.reduce(async (domainDataPromise: Promise<TaggingViewData>, node: FeatureNode) => {
      const domainData = await domainDataPromise
      const getFeatureConfigProvider = CONTAINER.get<FeatureConfigProvider>(
        TYPES.FeatureConfigProvider,
      )
      const { taggingConfig } = getFeatureConfigProvider(node.id as FeatureIdEnum, { domain })

      if (taggingConfig.id) {
        const dataItemRepeated = data.find(
          (dataItem: TaggingViewDataItem) => dataItem.id === taggingConfig.id,
        )
        if (dataItemRepeated) {
          const domainDataItem = await LoadViewsAction.getDomainData(
            domain,
            node.children,
            data,
            period,
          )
          dataItemRepeated.children.push(...domainDataItem)
        } else {
          const hasChildren = !!node.children?.length
          const dataItem = {
            domain,
            id: taggingConfig.id,
            nodeId: node.id as FeatureIdEnum,
            ...(!hasChildren && { context: taggingConfig.context }),
            count: 0,
            children: [],
          }
          const payload = TaggingUtils.getTaggingPayload(node.id as FeatureIdEnum, domain)
          if (hasChildren) {
            dataItem.children = await LoadViewsAction.getDomainData(
              domain,
              node.children,
              data,
              period,
            )
            dataItem.count = dataItem.children.reduce(
              (count: number, dataItemChild: TaggingViewDataItem) => count + dataItemChild.count,
              0,
            )
          } else if (!!payload && !!period) {
            try {
              const views = await LoadViewsAction.fetchData(payload, period)
              dataItem.count = views.count
            } catch (error) {
              console.error(error)
            }
          }
          domainData.push(dataItem)
        }
      }
      return domainData
    }, Promise.resolve([]))
  }

  private static async fetchData(
    payload: TaggingViewPayload,
    period: DateFilterSelectedValues,
  ): Promise<EngagementViewsData> {
    const store = CONTAINER.get<Store<any>>(TYPES.Store)
    const engagementViewsResponse: StatsResponse = await store.dispatch(
      `${ANALYTICS_CONSTS.moduleName}/${ANALYTICS_CONSTS.actions.fetchStatsReport}`,
      {
        id: StatsReportsIdsEnum.ENGAGEMENT_VIEWS,
        params: {
          startDate: new Date(period[0]),
          endDate: new Date(period[1]),
          ...payload,
        },
      },
      { root: true },
    )
    return engagementViewsResponse.data
  }
}
